//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import type { ChatMessage } from './AIModule';

// Prefix used to mark internal AI "log" chunks in the live chat history.
// Kept in sync with the LOG_PREFIX constant defined in AIModule/AIResponse.
export const TRANSCRIPT_LOG_PREFIX = ':::LOG:::';

/**
 * Convert the live in-memory chat history into a plain, JSON-safe transcript
 * suitable for download.
 *
 * `localChatHistories` holds React nodes (`subMessage`) and tool-call objects;
 * serializing those directly leaks the React element tree (`type`/`props`/`_owner`)
 * and tool internals into the download. This keeps only:
 *  - user / AI text messages,
 *  - a structured record of each tool call (name, status, args, result),
 *  - a structured record of each plan approval (decision, intent, domains,
 *    out-of-scope boundary),
 *  - a `sender: 'task'` marker for each task-selection divider so the transcript
 *    shows it is task mode, which task, and its description. Each task switch in
 *    the same session produces its own marker, segmenting the transcript.
 */
export const sanitizeChatForDownload = (
  messages: ChatMessage[],
  logPrefix: string = TRANSCRIPT_LOG_PREFIX,
): Array<Record<string, unknown>> => {
  const clean: Array<Record<string, unknown>> = [];

  for (const msg of messages) {
    if (msg.sender === 'tool') {
      clean.push({
        sender: 'tool',
        toolName: msg.toolName,
        toolStatus: msg.toolStatus,
        ...(msg.toolArgs && Object.keys(msg.toolArgs).length > 0
          ? { toolArgs: msg.toolArgs }
          : {}),
        ...(msg.toolResult ? { toolResult: msg.toolResult } : {}),
        // Which turn dispatched this call (FR-022). Absent for a call
        // recorded before this feature existed — the transcript still
        // includes it, just without turn attribution.
        ...(msg.turnId ? { turnId: msg.turnId } : {}),
      });
      continue;
    }

    if (msg.sender === 'plan_approval') {
      clean.push({
        sender: 'plan_approval',
        decision: msg.decision,
        intent: msg.plan.intent,
        domains: msg.plan.domains,
        outOfScope: msg.plan.out_of_scope,
        // Same stampTurn() mechanism as the tool branch above — this was
        // missing here even though plan_approval messages carry turnId the
        // same way (found in review of #988): a downloaded transcript could
        // attribute tool calls to their turn but not the plan-approval
        // decision that gated them.
        ...(msg.turnId ? { turnId: msg.turnId } : {}),
      });
      continue;
    }

    // Task-selection divider: emit a clear marker (mode + which task + description).
    if (msg.sender === 'ai' && msg.taskMeta) {
      clean.push({
        sender: 'task',
        taskName: msg.taskMeta.taskName,
        ...(msg.taskMeta.taskDescription
          ? { taskDescription: msg.taskMeta.taskDescription }
          : {}),
        ...(msg.taskMeta.taskId ? { taskId: msg.taskMeta.taskId } : {}),
      });
      continue;
    }

    // user | ai: keep plain text only — drop the React `subMessage`, the
    // streaming flag, and the internal log prefix. Skip entries that are pure
    // UI (e.g. quick-starter buttons / plan widgets) with no text.
    // Strip only a *leading* prefix: the marker is always prepended once at the
    // start of a log message, so a `:::LOG:::` appearing later is real content.
    const body = msg.message || '';
    const text = (
      body.startsWith(logPrefix) ? body.slice(logPrefix.length) : body
    ).trim();
    if (!text) continue;
    clean.push({ sender: msg.sender, message: text });
  }

  return clean;
};
